import PropTypes from 'prop-types';
import {jwtDecode} from 'jwt-decode'; // Fixed import
import { useMemo, useEffect, useReducer, useCallback } from 'react';
import { doc, getDoc, setDoc, collection, getFirestore } from 'firebase/firestore';
import {
  getAuth,
  signOut,
  signInWithPopup,
  onAuthStateChanged,
  GoogleAuthProvider,
  GithubAuthProvider,
  TwitterAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  browserSessionPersistence,
  setPersistence,
} from 'firebase/auth';
import axios from 'axios';
import { firebaseApp } from './lib';
import { AuthContext } from './auth-context';

const AUTH = getAuth(firebaseApp);

const DB = getFirestore(firebaseApp);

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  return state;
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const logUsage = useCallback(async (userId) => { // Memoized logUsage
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/UserUsage/log-usage/${userId}`);
    } catch (error) {
      console.error('Error logging usage:', error);
    }
  }, []);

  const initialize = useCallback(() => {
    setPersistence(AUTH, browserSessionPersistence)
      .then(() => {
        onAuthStateChanged(AUTH, async (user) => {
          if (user) {
            try {
              let profile = {};
              try {
                const userProfile = doc(DB, 'users', user.uid);
                const docSnap = await getDoc(userProfile);
                profile = docSnap.data() || {};
              } catch (profileError) {
                // A Firestore read failure (e.g. denied permissions) must not leave the
                // app stuck on the loading screen — continue with an empty profile so
                // the auth state still resolves.
                console.error('Error loading user profile:', profileError);
              }

              const idToken = await user.getIdToken(true);
              const decodedToken = jwtDecode(idToken);

              const role = decodedToken.role || ''; // Ensure role is set correctly

              dispatch({
                type: 'INITIAL',
                payload: {
                  user: {
                    ...user,
                    ...profile,
                    id: user.uid,
                    role,
                  },
                },
              });
            } catch (error) {
              // Any other failure during init still resolves loading rather than hanging.
              console.error('Error initializing auth state:', error);
              dispatch({
                type: 'INITIAL',
                payload: {
                  user: null,
                },
              });
            }
          } else {
            dispatch({
              type: 'INITIAL',
              payload: {
                user: null,
              },
            });
          }
        });
      })
      .catch((error) => {
        console.error('Error setting auth persistence:', error);
      });
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(AUTH, email, password);
    const userId = userCredential.user.uid;
    await logUsage(userId);
  }, [logUsage]);

  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(AUTH, provider);
  }, []);

  const loginWithGithub = useCallback(async () => {
    const provider = new GithubAuthProvider();
    await signInWithPopup(AUTH, provider);
  }, []);

  const loginWithTwitter = useCallback(async () => {
    const provider = new TwitterAuthProvider();
    await signInWithPopup(AUTH, provider);
  }, []);

  // // REGISTER
  // const register = useCallback(async (email, password, firstName, lastName) => {
  //   const newUser = await createUserWithEmailAndPassword(AUTH, email, password);
  //   await sendEmailVerification(newUser.user);
  //   const userProfile = doc(collection(DB, 'users'), newUser.user?.uid);
  //   await setDoc(userProfile, {
  //     uid: newUser.user?.uid,
  //     email,
  //     displayName: `${firstName} ${lastName}`,
  //   });
  // }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    await signOut(AUTH);
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email) => {
    await sendPasswordResetEmail(AUTH, email);
  }, []);

  const checkAuthenticated = state.user?.emailVerified ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedUser = useMemo(() => ({
    uid: state.user?.uid,
    displayName: state.user?.displayName,
    role: state.user?.role,
  }), [state.user]);



  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'firebase',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      role: state.user?.role,
      memoizedUser, // Add memoizedUser to context
      login,
      logout,
      // register,
      forgotPassword,
      loginWithGoogle,
      loginWithGithub,
      loginWithTwitter,
    }),
    [
      status,
      state.user,
      memoizedUser, // Add dependency
      login,
      logout,
      // register,
      forgotPassword,
      loginWithGithub,
      loginWithGoogle,
      loginWithTwitter,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
